import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/use-toast';
import type { ScheduledJobItem } from '../services/scheduled-jobs.types';
import { useDeleteScheduledJob } from './use-scheduled-jobs';

/** Delete and Open Chat for a routine, shared by the lists and the linked routine dialog. */
export function useScheduledJobActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const deleteMutation = useDeleteScheduledJob();

  /** Soft delete with the result toast. Rethrows so the dialog can stay open on failure. */
  const deleteJob = async (job: ScheduledJobItem) => {
    try {
      await deleteMutation.mutateAsync(job.id);
      toast({
        title: t('SCHEDULED_JOB_DELETED', { defaultValue: 'Scheduled job deleted' }),
        description: t('SCHEDULED_JOB_DELETED_DESCRIPTION', {
          defaultValue: 'The job was removed. Run history is preserved.',
        }),
      });
    } catch (error) {
      console.error('Failed to delete scheduled job:', error);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_DELETE_SCHEDULED_JOB', {
          defaultValue: 'Failed to delete scheduled job',
        }),
      });
      throw error;
    }
  };

  const openChat = (job: ScheduledJobItem) => {
    if (job.conversationId) {
      navigate(`/chat/${job.conversationId}`);
      return;
    }
    if (job.agentId) {
      navigate(`/chat?agentId=${encodeURIComponent(job.agentId)}`);
    }
  };

  return { deleteJob, openChat, isDeleting: deleteMutation.isPending };
}
